/**
 * Global Help Center context — open the modal (optionally to a specific article)
 * from header button, page headers, and inline info icons.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useConfig } from '../../contexts/ConfigContext';
import { HelpCenter } from './HelpCenter';
import { helpContentRegistry } from './helpContentRegistry';
import type { HelpArticleData } from './types';

export interface HelpCenterContextValue {
  openHelpCenter: (articleId?: string) => void;
  closeHelpCenter: () => void;
  isOpen: boolean;
}

const HelpCenterContext = createContext<HelpCenterContextValue | null>(null);

export interface HelpCenterProviderProps {
  children: ReactNode;
  /** Extra articles passed alongside registry content (app-specific overrides). */
  customArticles?: HelpArticleData[];
}

export function HelpCenterProvider({
  children,
  customArticles = [],
}: HelpCenterProviderProps) {
  const config = useConfig();
  const supportEmail = config?.customization?.supportEmail as string | undefined;
  const [isOpen, setIsOpen] = useState(false);
  const [initialArticleId, setInitialArticleId] = useState<string | null>(null);
  const [registeredArticles, setRegisteredArticles] = useState<HelpArticleData[]>([]);

  useEffect(() => {
    setRegisteredArticles(helpContentRegistry.getAllArticles());
    return helpContentRegistry.subscribe(setRegisteredArticles);
  }, []);

  const allCustomArticles = useMemo(
    () => [...registeredArticles, ...customArticles],
    [registeredArticles, customArticles],
  );

  const openHelpCenter = useCallback((articleId?: string) => {
    setInitialArticleId(articleId ?? null);
    setIsOpen(true);
  }, []);

  const closeHelpCenter = useCallback(() => {
    setIsOpen(false);
    setInitialArticleId(null);
  }, []);

  const value = useMemo<HelpCenterContextValue>(
    () => ({
      openHelpCenter,
      closeHelpCenter,
      isOpen,
    }),
    [openHelpCenter, closeHelpCenter, isOpen],
  );

  return (
    <HelpCenterContext.Provider value={value}>
      {children}
      <HelpCenter
        isOpen={isOpen}
        onClose={closeHelpCenter}
        customArticles={allCustomArticles}
        initialArticleId={initialArticleId}
        supportEmail={supportEmail}
      />
    </HelpCenterContext.Provider>
  );
}

export function useHelpCenter(): HelpCenterContextValue {
  const context = useContext(HelpCenterContext);
  if (!context) {
    throw new Error('useHelpCenter must be used within a HelpCenterProvider');
  }
  return context;
}

export function useOptionalHelpCenter(): HelpCenterContextValue | null {
  return useContext(HelpCenterContext);
}
